import { ActionDropdownButton } from "components/DropdownButton/ActionDropdownButton";
import ElementLoader from "components/ElementLoader";
import customObjectConfig from "config/resources/custom_object";
import executionConfig from "config/resources/execution";
import {
    changePasswordKey,
    deleteUserKey,
    disableTwoFAKey,
    enableTwoFAKey,
} from "config/resources/user";
import {
    reInstallBundleActionKey,
    releaseBundleActionKey,
    uninstallBundleActionKey,
    updateBundleActionKey,
} from "containers/BundleManager/Actions";
import { ActionData, ActionSignature } from "containers/Resources/components/CommonActions/Action";
import ActionButton from "containers/Resources/components/CommonActions/ActionButton";
import { migrateDeprecationsKey } from "containers/Resources/components/CommonActions/CommonActions";
import { bulkCommitActionKey } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/BulkCommitAction";
import { commitActionKey } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/CommitAction";
import { AnyObject } from "final-form";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

interface ShownActionButtonProps {
    action: ActionSignature;
    actionData: ActionData;
    hasValidationErrors?: boolean;
    formPristine: boolean;
    resourceName: string;
    isLoading: boolean;
    saveForm: () => Promise<AnyObject>;
    fetchResourceData: () => void;
    saveNestedFormsWithData: () => Promise<void>;
    isGitLocked?: boolean;
}
export const ShownActionButton = (props: ShownActionButtonProps) => {
    const {
        action,
        actionData,
        hasValidationErrors,
        formPristine,
        isLoading,
        saveForm,
        saveNestedFormsWithData,
        fetchResourceData,
        resourceName,
    } = props;
    const dispatch = useDispatch();
    const history = useHistory();
    const [isActionInFlight, setActionInFlight] = useState(false);
    const skipSubmit = action?.submitFormOnAction === false;
    if (action.omitWhen && action.omitWhen(actionData)) return null;
    const isDisabled = !!(
        (skipSubmit ? false : hasValidationErrors) ||
        (action.disableWhen && action.disableWhen(actionData)) ||
        (action.key === commitActionKey && !formPristine)
    );
    const saveFormData = () => (formPristine ? saveNestedFormsWithData() : saveForm());
    const dropdownContentActionData = {
        ...actionData,
        afterAction: action.hasDropdownContentAfterAction ? fetchResourceData : undefined,
    };
    const actionHandler = () => {
        setActionInFlight(true);
        const complete = () => setActionInFlight(false);
        const afterAction = () => {
            if (
                action?.key !== deleteUserKey &&
                action?.key !== disableTwoFAKey &&
                action?.key !== enableTwoFAKey &&
                action?.key !== migrateDeprecationsKey &&
                action?.key !== releaseBundleActionKey &&
                action?.key !== updateBundleActionKey &&
                action?.key !== reInstallBundleActionKey &&
                action?.key !== uninstallBundleActionKey &&
                resourceName !== executionConfig.resourceName &&
                resourceName !== customObjectConfig.resourceName &&
                action?.key !== changePasswordKey
            ) {
                fetchResourceData();
            }
        };

        if (formPristine) {
            return saveNestedFormsWithData()
                .then(() => action?.action(actionData, dispatch, history))
                .then(() => afterAction())
                .finally(complete);
        }
        if (skipSubmit) {
            return Promise.resolve(action.action(actionData, dispatch, history)).finally(complete);
        }
        const savePromise = saveForm()?.then((data) =>
            action.action(
                {
                    ...actionData,
                    resourceData: data ?? actionData.resourceData,
                },
                dispatch,
                history
            )
        );
        if (!savePromise) {
            complete();
            return undefined;
        }
        return savePromise.then(() => fetchResourceData()).finally(complete);
    };

    const isGitActionLoading =
        (action?.key === commitActionKey || action?.key === bulkCommitActionKey) &&
        actionData?.isGitLocked;
    const isButtonLoading = isLoading || isGitActionLoading || isActionInFlight;
    if (action?.isDropdown?.(actionData) && action?.dropdownContent) {
        const { dropdownContent } = action;
        const loadingIcon = isButtonLoading
            ? () => (
                  <div className="actionButtonLoader">
                      <ElementLoader />
                  </div>
              )
            : null;
        return (
            <ActionDropdownButton
                parentAction={actionHandler}
                label={action.label(actionData)}
                icon={loadingIcon ?? action.icon(actionData)}
                isDisabled={isDisabled || isActionInFlight}
                isLoading={isButtonLoading}
                key={`action${action?.key}`}
                dropdownIcon={action.dropdownIcon?.(actionData)}
                tooltipOverlay={action.tooltipOverlay?.(actionData)}
                image={action.image?.(actionData)}
            >
                {dropdownContent({
                    actionData: dropdownContentActionData,
                    dispatch,
                    history,
                    isDisabled,
                    saveForm: saveFormData,
                })}
            </ActionDropdownButton>
        );
    }
    const actionButton = (
        <ActionButton
            key={`action${action?.key}`}
            label={action.label(actionData)}
            icon={action.icon(actionData)}
            image={action.image?.(actionData)}
            action={actionHandler}
            disabled={isDisabled || isActionInFlight}
            loading={isButtonLoading}
        />
    );
    const TooltipComponent = action.tooltipComponent;

    return TooltipComponent ? <TooltipComponent>{actionButton}</TooltipComponent> : actionButton;
};
